// Attachments, 10 MB in total; shows the remaining allowance. Refuses executables, unreadable
// files and anything over the cap. Updates are functional so rapid selections never race.
import { Paperclip, X } from "lucide-react";
import { useLocaleState, useNotify, useTranslate } from "ra-core";
import { useEffect, useRef, type Dispatch, type SetStateAction } from "react";
import { Button } from "@/components/ui/button";
import type { ComposeAttachment } from "../api/types";
import {
  canAdd,
  formatMb,
  isBlockedFile,
  readFileAsAttachment,
  remainingBytes,
} from "./composeModel";

type Props = {
  value: ComposeAttachment[];
  onChange: Dispatch<SetStateAction<ComposeAttachment[]>>;
  disabled?: boolean;
};

export const AttachmentsInput = ({ value, onChange, disabled }: Props) => {
  const t = useTranslate();
  const notify = useNotify();
  const [locale = "fr"] = useLocaleState();
  // Latest list including additions not yet rendered, so the cap holds across rapid selections.
  const latest = useRef(value);
  useEffect(() => {
    latest.current = value;
  }, [value]);

  const addOne = async (f: File) => {
    if (isBlockedFile(f.name)) {
      notify("gavion.compose.attachment_blocked", {
        type: "error",
        messageArgs: { name: f.name },
      });
      return;
    }
    let att: ComposeAttachment;
    try {
      att = await readFileAsAttachment(f);
    } catch {
      notify("gavion.compose.attachment_read_failed", {
        type: "error",
        messageArgs: { name: f.name },
      });
      return;
    }
    if (!canAdd(latest.current, att.size)) {
      notify("gavion.integrations.errors.attachments_too_large", {
        type: "error",
      });
      return;
    }
    latest.current = [...latest.current, att];
    onChange((prev) => [...prev, att]);
  };
  const add = async (files: File[]) => {
    for (const f of files) await addOne(f);
  };

  return (
    <div className="flex flex-col gap-1 text-sm">
      <span className="font-medium">
        {t("gavion.compose.attachments")}{" "}
        <span className="font-normal text-muted-foreground">
          ({t("gavion.compose.attachments_limit")})
        </span>
      </span>
      <span className="text-xs text-muted-foreground" aria-live="polite">
        {t("gavion.compose.attachments_remaining", {
          size: formatMb(remainingBytes(value), locale),
        })}
      </span>
      <ul className="flex flex-col gap-1">
        {value.map((a, i) => (
          <li
            key={`${i}-${a.name}`}
            className="flex min-w-0 items-center gap-2"
          >
            <Paperclip className="size-3 shrink-0" aria-hidden="true" />
            <span className="truncate">{a.name}</span>
            <span className="shrink-0 text-xs text-muted-foreground">
              {formatMb(a.size, locale)}
            </span>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              disabled={disabled}
              aria-label={t("gavion.compose.remove_attachment", {
                name: a.name,
              })}
              onClick={() => onChange((prev) => prev.filter((_, j) => j !== i))}
            >
              <X className="size-3" aria-hidden="true" />
            </Button>
          </li>
        ))}
      </ul>
      <label className="cursor-pointer self-start text-primary underline">
        {t("gavion.compose.add_attachment")}
        <input
          type="file"
          multiple
          disabled={disabled}
          className="sr-only"
          onChange={(e) => {
            const files = Array.from(e.target.files ?? []);
            e.target.value = "";
            void add(files);
          }}
        />
      </label>
    </div>
  );
};
